import { z } from 'zod'
import { asObjectType } from '@gqloom/zod'

import { documentFileConfigFieldSchema } from '../file-upload/document.schemas'
import { generateFileUploadSignedUrlResponseSchema } from '../file-upload/file-upload.schemas'

export const clientFileSchema = z
  .object({
    fileId: z.string().uuid(),
    clientId: z.string().uuid(),
    siteId: z.string().uuid(),
    fileName: z.string(),
    fileType: z.string(),
    url: z.string(),
    createdAt: z.coerce.date().nullable().optional(),
    uploadedAt: z.coerce.date(),
    fileJSON: documentFileConfigFieldSchema,
  })
  .register(asObjectType, {
    name: 'ClientFile',
  })

export const createClientFileInputSchema = z
  .object({
    fileName: z.string(),
    format: z.string(),
  })
  .register(asObjectType, {
    name: 'CreateClientFileInput',
  })

export const createClientFileResponseSchema = z
  .object({
    fileId: z.uuid().describe('ID of the created record, to upload the document against'),
  })
  .register(asObjectType, {
    name: 'CreateClientFileResponse',
  })

/**
 * The signed-URL response plus the server-side `fileId`, which the client must echo back to
 * `completeClientFileUpload` once the PUT lands. The shared
 * `generateFileUploadSignedUrlResponseSchema` is a federated `shareable` type consumed by
 * several services, so the id is added here rather than widening that contract.
 */
export const generateClientFileUploadUrlResponseSchema = generateFileUploadSignedUrlResponseSchema
  .extend({
    fileId: z.uuid(),
  })
  .register(asObjectType, {
    name: 'GenerateClientFileUploadUrlResponse',
  })

export const completeClientFileUploadResponseSchema = z
  .object({
    fileId: z.uuid(),
    success: z.boolean(),
  })
  .register(asObjectType, {
    name: 'CompleteClientFileUploadResponse',
  })

export const clientFileUrlSchema = z
  .object({
    url: z.string(),
  })
  .register(asObjectType, {
    name: 'ClientFileUrl',
  })

export const deleteClientFileResponseSchema = z
  .object({
    success: z.boolean(),
  })
  .register(asObjectType, {
    name: 'DeleteClientFileResponse',
  })

export const updateClientFileNameResponseSchema = z
  .object({
    success: z.boolean(),
    message: z.string().optional(),
  })
  .register(asObjectType, {
    name: 'UpdateClientFileNameResponse',
  })

export const clientFilesListSchema = z.array(clientFileSchema).register(asObjectType, {
  name: 'ClientFilesList',
})

export type ClientFileSchema = z.infer<typeof clientFileSchema>
export type CreateClientFileInputSchema = z.infer<typeof createClientFileInputSchema>
export type ClientFileUrlSchema = z.infer<typeof clientFileUrlSchema>
export type GenerateClientFileUploadUrlResponseSchema = z.infer<
  typeof generateClientFileUploadUrlResponseSchema
>
export type CompleteClientFileUploadResponseSchema = z.infer<
  typeof completeClientFileUploadResponseSchema
>
export type DeleteClientFileResponseSchema = z.infer<typeof deleteClientFileResponseSchema>
export type UpdateClientFileNameResponseSchema = z.infer<typeof updateClientFileNameResponseSchema>
export type ClientFilesListSchema = z.infer<typeof clientFilesListSchema>
